import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Info, Loader2 } from "lucide-react";

import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { Button } from "@/components/ui/button";
import { CertificateStepContent } from "@/pages/onboarding/CertificateStepContent";
import { OnboardingAwaitingCertificateStep } from "@/pages/onboarding/OnboardingAwaitingCertificateStep";
import { FiscaloCertificateConfirmCard } from "@/pages/onboarding/FiscaloCertificateConfirmCard";
import { OnboardingAbitabPresentStep } from "@/pages/onboarding/OnboardingAbitabPresentStep";
import { LiteralEStep } from "@/pages/onboarding/LiteralEStep";
import { MigrationChecklistStep } from "@/pages/onboarding/MigrationChecklistStep";
import { AccountantSuccessStep } from "@/pages/onboarding/AccountantSuccessStep";
import { OwnerSuccessStep } from "@/pages/onboarding/OwnerSuccessStep";
import { PersonalizeStep } from "@/pages/onboarding/PersonalizeStep";
import { PlanStep } from "@/pages/onboarding/PlanStep";
import { RegimeStep } from "@/pages/onboarding/RegimeStep";
import { OnboardingHomologacionPendingStep } from "@/pages/onboarding/OnboardingHomologacionPendingStep";
import { ReadyStep } from "@/pages/onboarding/ReadyStep";
// OnboardingIntroStep disabled — merged into WhoSetupStep (step 0/1 combined)
import { OnboardingSituationStep } from "@/pages/onboarding/OnboardingSituationStep";
import { WhoSetupStep } from "@/pages/onboarding/WhoSetupStep";
import { DgiAccessStep } from "@/pages/onboarding/DgiAccessStep";
import { OnboardingCaeStep } from "@/pages/onboarding/OnboardingCaeStep";
import { OnboardingCfeImportStep } from "@/pages/onboarding/OnboardingCfeImportStep";
import { CertificateAcquisitionStep } from "@/pages/onboarding/CertificateAcquisitionStep";
import { OnboardingStep } from "@/pages/onboarding/OnboardingStep";
import type { OnboardingContext } from "@/pages/onboarding/useOnboarding";

export interface OnboardingStepContentProps {
  onboarding: OnboardingContext;
}

export function OnboardingStepContent({
  onboarding,
}: OnboardingStepContentProps): JSX.Element {
  const { t } = useTranslation();
  const {
    viewStep,
    tenant,
    user,
    isAccountantFlowOwner,
    isAccountantUser,
    isMigrating,
    totalSteps,
    situationChoice,
    setViewStep,
    needsCfeImportStep,
    ownerPostCertViewSteps,
    situationStep,
    dgiStep,
    certChoiceStep,
    certContentStep,
    abitabStep,
    missingOnboardingCaeCfeTypes,
    onboardingCaeListRefreshing,
  } = onboarding;

  const isAlreadyIssuer =
    situationChoice === "already_issuer" ||
    onboarding.tenant?.onboardingSituationChoice === "already_issuer";
  const step = (n: number): boolean => viewStep === n + 1;

  if (viewStep === 0 || viewStep === 1) {
    const companyDisplayName =
      onboarding.tenant?.legalName?.trim() ||
      onboarding.tenant?.name?.trim() ||
      "";
    return (
      <OnboardingStep name="who_setup" onboarding={onboarding}>
        <WhoSetupStep
          selected={onboarding.whoSetupChoice}
          onSelect={onboarding.setWhoSetupChoice}
          onSubmit={onboarding.handleWhoSetupSubmit}
          isSaving={onboarding.savingWhoSetup}
          companyDisplayName={companyDisplayName}
          accountantEmail={onboarding.accountantInviteEmail}
          onAccountantEmailChange={onboarding.setAccountantInviteEmail}
        />
      </OnboardingStep>
    );
  }

  // Pre-DGI plan gate: owner-self users must pick a plan before any other
  // onboarding step (situation, DGI access, cert, etc.). MUST run before
  // the viewStep-specific branches below so the gate overrides whatever
  // would normally render at viewStep=2/3/4/etc.
  // Régimen gate: ask the Decreto 206/019 declaration BEFORE plans, because the
  // answer decides which plans are eligible and what they cost. Runs before the
  // plan gate; once the declaration is persisted (fiscalBenefitDeclared) this
  // clears and the plan gate takes over.
  // Self-scratch parked on homologación: the user did the safe self-serve part
  // (created the DGI e-Factura user); the team is completing homologación. This
  // overrides the normal step until homologacionStatus reaches completed/vigente,
  // after which the wizard resumes at certificate → CAE.
  if (onboarding.needsHomologacionPending) {
    return (
      <OnboardingStep name="homologacion_pending" onboarding={onboarding}>
        <OnboardingHomologacionPendingStep tenant={onboarding.tenant} />
      </OnboardingStep>
    );
  }

  if (onboarding.needsRegimeGate) {
    return (
      <OnboardingStep name="regime" onboarding={onboarding}>
        <RegimeStep
          value={onboarding.benefitDeclaration}
          onChange={onboarding.setBenefitDeclaration}
          onContinue={onboarding.handleRegimeGateContinue}
          onBack={onboarding.handleBackStep}
          isSaving={onboarding.nextButtonLoading}
        />
      </OnboardingStep>
    );
  }

  // Free plan saves instantly; paid plans redirect to dLocal Go and
  // return with planId set (clearing this gate).
  if (onboarding.needsPlanGate) {
    return (
      <OnboardingStep name="plan_gate" onboarding={onboarding}>
        <OnboardingShell
          variant="single"
          form={
            <>
              <OnboardingBrandMark />
              <OnboardingStepTag
                step={2}
                total={2}
                label={t("onboarding.planStep.tag", "Plan")}
                flow={t("onboarding.planPhase.flow", "Tu plan")}
              />
              <PlanStep
                plans={onboarding.plans}
                partnerBenefit={onboarding.partnerBenefit}
                selectedPlanId={onboarding.selectedPlanId}
                selectedBillingInterval={onboarding.selectedBillingInterval}
                setSelectedBillingInterval={onboarding.setSelectedBillingInterval}
                onSelectPlan={onboarding.setSelectedPlanId}
                eligiblePlanIds={onboarding.tenant?.eligiblePlanIds ?? null}
              />
              <div className="fx-onb-actions" style={{ marginTop: 28 }}>
                <div className="side">
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onboarding.handleBackStep}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                </div>
                <div className="side end">
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-accent"
                    onClick={() => void onboarding.handlePlanGateContinue()}
                    disabled={
                      onboarding.nextButtonDisabled ||
                      onboarding.nextButtonLoading
                    }
                  >
                    {onboarding.nextButtonLoading ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {t("onboarding.next", "Siguiente")}
                    <ArrowRight aria-hidden />
                  </button>
                </div>
              </div>
            </>
          }
        />
      </OnboardingStep>
    );
  }

  if (viewStep === situationStep) {
    const companyDisplayName =
      onboarding.tenant?.legalName?.trim() ||
      onboarding.tenant?.name?.trim() ||
      "";
    return (
      <OnboardingStep name="situation" onboarding={onboarding}>
        <OnboardingSituationStep
          companyDisplayName={companyDisplayName}
          tenantRut={onboarding.tenant?.rut ?? null}
          isMigratingCfes={needsCfeImportStep}
          onDetectEmisorStatus={onboarding.handleDetectEmisorStatus}
          onSubmit={onboarding.handleSubmitSituation}
          onBack={onboarding.handleBackStep}
          isSaving={onboarding.nextButtonLoading}
        />
      </OnboardingStep>
    );
  }

  if (viewStep === dgiStep) {
    const legalName =
      onboarding.tenant?.legalName?.trim() ||
      onboarding.tenant?.name?.trim() ||
      "";
    return (
      <OnboardingStep name="dgi_access" onboarding={onboarding}>
        <DgiAccessStep
          legalName={legalName}
          tenantId={onboarding.tenant?.id ?? null}
          hasDgiPortalUser={onboarding.tenant?.hasDgiPortalUser ?? false}
          hasCfeImportNext={needsCfeImportStep}
          situationChoice={onboarding.situationChoice}
          onContinue={onboarding.handleContinueFromDgi}
          onBack={onboarding.handleBackStep}
          onPasswordReady={() => onboarding.setDgiPasswordReady?.(true)}
          onStepsCompletedConfirmed={(v) =>
            onboarding.setDgiStepsCompletedConfirmed?.(v)
          }
          stepsCompletedConfirmed={onboarding.dgiStepsCompletedConfirmed ?? false}
          dgiPortalUserConfirmed={
            Boolean(onboarding.tenant?.dgiPortalUserConfirmed) ||
            Boolean(onboarding.tenant?.dgiPortalUserConfirmedAt) ||
            // Once the Python automation has submitted Solicitar Usuario to DGI
            // (progress.portal_user set), we treat the step as done from the
            // auto-flow's perspective — DGI's email confirmation still has to
            // happen separately, but we mustn't replay the creation on refresh.
            Boolean(
              (onboarding.tenant?.dgiAutoOnboardingProgress as
                | Record<string, string>
                | null
                | undefined)?.portal_user,
            )
          }
          dgiStep3SuccessToastAcknowledged={
            onboarding.dgiStep3SuccessToastAcknowledged
          }
          onRefreshTenant={onboarding.loadTenant}
          dgiAutoOnboardingProgress={
            onboarding.tenant?.dgiAutoOnboardingProgress ?? null
          }
        />
      </OnboardingStep>
    );
  }

  if (viewStep === certChoiceStep && !isAccountantFlowOwner && !isAccountantUser) {
    return (
      <OnboardingStep name="cert_choice" onboarding={onboarding}>
        <CertificateAcquisitionStep
          mode="choice"
          selectedChoice={onboarding.certificateAcquisitionChoice}
          onChoiceChange={onboarding.setCertificateAcquisitionChoice}
          onContinue={onboarding.handleNextStep}
          onBack={onboarding.handleBackStep}
          isSaving={onboarding.nextButtonLoading}
          hasCfeImportNext={needsCfeImportStep}
          situationChoice={onboarding.situationChoice}
        />
      </OnboardingStep>
    );
  }

  if (viewStep === certContentStep && !isAccountantFlowOwner && !isAccountantUser) {
    return (
      <OnboardingStep name="cert_content" onboarding={onboarding}>
        <CertificateAcquisitionStep
          mode="content"
          choice={onboarding.certificateAcquisitionChoice ?? undefined}
          companyType={onboarding.tenant?.companyType ?? null}
          onCanAdvanceChange={onboarding.setCertificateAcquisitionCanAdvance}
          onCertificateRequestPayload={onboarding.setCertificateRequestPayload}
          currentCertificateRequest={onboarding.currentCertificateRequest ?? null}
          currentCertificateRequestLoading={
            onboarding.currentCertificateRequestLoading ?? false
          }
          hasCfeImportNext={needsCfeImportStep}
          situationChoice={onboarding.situationChoice}
          onContinue={onboarding.handleNextStep}
          onBack={onboarding.handleBackStep}
          isSaving={onboarding.nextButtonLoading}
          tenantSnapshot={
            onboarding.tenant
              ? {
                  rut: onboarding.tenant.rut ?? null,
                  legalName: onboarding.tenant.legalName ?? null,
                  tradeName: onboarding.tenant.name ?? null,
                  phone: onboarding.tenant.phone ?? null,
                }
              : null
          }
          onSubmitFiscaloRequest={onboarding.handleConfirmFiscaloCertificate}
        />
      </OnboardingStep>
    );
  }

  // The old delegated-owner wizard (invite accountant on step 3, then a
  // "waiting for accountant" progress screen on step 4) is gone: the owner
  // now enters the accountant email on WhoSetup, passes the plan gate, and
  // is routed to the dashboard, where DashboardOnboardingCard (delegated mode,
  // with the accountant-handoff banner) owns the invite/status UI. The
  // step-sync short-circuit in useOnboarding never
  // lands a delegated owner on these viewSteps anymore.

  if (step(5) && isAccountantFlowOwner && onboarding.tenant) {
    return (
      <OnboardingStep name="acc_owner_personalize" onboarding={onboarding}>
        <PersonalizeStep
          tenant={onboarding.tenant}
          legalName={onboarding.legalName}
          setLegalName={onboarding.setLegalName}
          onLogoFileChange={onboarding.handleLogoFileChange}
          uploadingLogo={onboarding.uploadingLogo}
        />
      </OnboardingStep>
    );
  }

  if (step(6) && isAccountantFlowOwner) {
    return (
      <OnboardingStep name="acc_owner_plan" onboarding={onboarding}>
        <OnboardingShell
          variant="single"
          form={
            <>
              <OnboardingBrandMark />
              <PlanStep
                plans={onboarding.plans}
                partnerBenefit={onboarding.partnerBenefit}
                selectedPlanId={onboarding.selectedPlanId}
                selectedBillingInterval={onboarding.selectedBillingInterval}
                setSelectedBillingInterval={onboarding.setSelectedBillingInterval}
                onSelectPlan={onboarding.setSelectedPlanId}
                eligiblePlanIds={onboarding.tenant?.eligiblePlanIds ?? null}
              />
              <div className="fx-onb-actions" style={{ marginTop: 28 }}>
                <div className="side">
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onboarding.handleBackStep}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                </div>
                <div className="side end">
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-accent"
                    onClick={() => void onboarding.handleNextStep()}
                    disabled={
                      onboarding.nextButtonDisabled ||
                      onboarding.nextButtonLoading
                    }
                  >
                    {onboarding.nextButtonLoading ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {t("onboarding.next", "Siguiente")}
                    <ArrowRight aria-hidden />
                  </button>
                </div>
              </div>
            </>
          }
        />
      </OnboardingStep>
    );
  }

  if (step(7) && isAccountantFlowOwner) {
    return (
      <OnboardingStep name="acc_owner_success" onboarding={onboarding}>
        <OwnerSuccessStep
          onGoToDashboard={onboarding.handleFinish}
          finishing={onboarding.finishing}
        />
      </OnboardingStep>
    );
  }

  if (step(7) && isAccountantUser) {
    return (
      <OnboardingStep name="acc_user_success" onboarding={onboarding}>
        <AccountantSuccessStep
          onGoToDashboard={onboarding.handleFinish}
          finishing={onboarding.finishing}
        />
      </OnboardingStep>
    );
  }

  if (viewStep === abitabStep && tenant && !isAccountantUser) {
    if (onboarding.certificateAcquisitionChoice === "fiscalo") {
      const showConfirm =
        !onboarding.currentCertificateRequest &&
        !onboarding.currentCertificateRequestLoading &&
        onboarding.step6FiscaloConfirmPayload != null;
      if (showConfirm && onboarding.step6FiscaloConfirmPayload) {
        return (
          <OnboardingStep name="abitab" onboarding={onboarding}>
            <FiscaloCertificateConfirmCard
              certificateRequestPayload={onboarding.step6FiscaloConfirmPayload}
              documentNumber={onboarding.user?.documentNumber ?? null}
              documentType={onboarding.user?.documentType ?? null}
              rut={tenant.rut ?? null}
              legalName={tenant.legalName ?? tenant.name ?? null}
              tradeName={tenant.name ?? tenant.legalName ?? null}
              defaultPhone={tenant.phone ?? null}
              onConfirm={onboarding.handleConfirmFiscaloCertificate}
              confirming={onboarding.certificateStepNextLoading}
            />
          </OnboardingStep>
        );
      }
      const step6AwaitingAbitab = Boolean(tenant.step6AbitabAcknowledgedAt);
      if (step6AwaitingAbitab) {
        const paymentReceived = Boolean(
          onboarding.currentCertificateRequest?.paymentReceivedAt,
        );
        const certificateReady =
          onboarding.currentCertificateRequest?.status === "fulfilled";
        // Unlabelled, like the OnboardingAbitabPresentStep return below it.
        // Both are sub-states of the one `viewStep === abitabStep` branch, and
        // onboardingStepLabels.test.ts counts one label per TOP-LEVEL branch —
        // labelling a sub-state breaks that equality, and relaxing it to >=
        // would stop the guard catching a genuinely unlabelled branch. Giving
        // every abitab sub-state its own analytics name is worth doing, but it
        // needs the guard taught about sub-branches first.
        return (
          <OnboardingAwaitingCertificateStep
            paymentReceived={paymentReceived}
            certificateReady={certificateReady}
            onContinue={() => {
              // Record the choice BEFORE advancing. The write is
              // fire-and-forget, so the order only decides whether a
              // same-tick refresh keeps their place — never whether the
              // button works.
              if (!certificateReady) onboarding.markCertWaitContinued();
              void onboarding.handleNextStep();
            }}
            situationChoice={onboarding.situationChoice}
            hasCfeImportNext={needsCfeImportStep}
          />
        );
      }
      // The cert request exists. Render the editorial Abitab present
      // step in both states: request-number-present (status="in_progress"
      // with idDigitalRequestNumber) and request-number-pending (the
      // automator hasn't returned the number yet). The component handles
      // the pending variant internally — see isPending branch.
      return (
        <OnboardingAbitabPresentStep
          idDigitalRequestNumber={
            onboarding.currentCertificateRequest?.idDigitalRequestNumber ??
            null
          }
          certificateRequestStatus={
            onboarding.currentCertificateRequest?.status ?? null
          }
          onRetryAutomation={onboarding.handleRetryCertificateAutomation}
          retrying={onboarding.certificateAutomationRetrying ?? false}
          comprobantePdfUrl={
            onboarding.currentCertificateRequest?.comprobantePdfUrl ?? null
          }
          companyType={tenant.companyType ?? null}
          situationChoice={onboarding.situationChoice}
          hasCfeImportNext={needsCfeImportStep}
          onNotarialCanAdvanceChange={onboarding.setStep6NotarialCanAdvance}
          notarialCanAdvance={onboarding.step6NotarialCanAdvance ?? false}
          onBack={onboarding.handleBackStep}
          onNext={onboarding.handleNextStep}
          nextDisabled={onboarding.nextButtonDisabled}
          nextLoading={onboarding.nextButtonLoading}
        />
      );
    }
    // Continue handler for the cert "already uploaded" success view —
    // advance to the next post-cert step (CAE if missing, else CFE
    // import if needed, else viewStep+1 as a fallback so the sync can
    // route from there).
    const handleContinueFromCertDone = (): void => {
      if (ownerPostCertViewSteps.cae != null && missingOnboardingCaeCfeTypes.length > 0) {
        onboarding.setViewStep(ownerPostCertViewSteps.cae);
        return;
      }
      if (
        needsCfeImportStep &&
        ownerPostCertViewSteps.cfe != null &&
        !tenant.onboardingCfeImportStepCompletedAt
      ) {
        onboarding.setViewStep(ownerPostCertViewSteps.cfe);
        return;
      }
      onboarding.setViewStep(viewStep + 1);
    };
    return (
      <CertificateStepContent
        hasDgiCredentials={tenant.hasDgiCredentials}
        tenantId={tenant.id}
        tenantRut={tenant.rut ?? null}
        certificatePath={onboarding.certificatePath}
        setCertificatePath={onboarding.setCertificatePath}
        fiscaloCertSubstep={onboarding.fiscaloCertSubstep}
        setFiscaloCertSubstep={onboarding.setFiscaloCertSubstep}
        onSuccess={onboarding.handleCertificateUploadSuccess}
        alreadyIssuerHint={isAlreadyIssuer}
        hasCfeImportNext={needsCfeImportStep}
        situationChoice={onboarding.situationChoice}
        onContinueFromDone={handleContinueFromCertDone}
        onSkip={onboarding.handleSkipDgiCertificate}
        skipLoading={onboarding.skipDgiCertificateLoading}
        onBackToPathChoice={
          isAlreadyIssuer
            ? onboarding.setViewStep.bind(null, dgiStep)
            : onboarding.setViewStep.bind(null, certContentStep)
        }
      />
    );
  }

  if (step(6) && isAccountantUser && tenant) {
    return (
      <OnboardingStep name="acc_user_cert" onboarding={onboarding}>
        <CertificateStepContent
          hasDgiCredentials={tenant.hasDgiCredentials}
          tenantId={tenant.id}
          tenantRut={tenant.rut ?? null}
          certificatePath={onboarding.certificatePath}
          setCertificatePath={onboarding.setCertificatePath}
          fiscaloCertSubstep={onboarding.fiscaloCertSubstep}
          setFiscaloCertSubstep={onboarding.setFiscaloCertSubstep}
          onSuccess={onboarding.handleCertificateUploadSuccess}
          hasCfeImportNext={needsCfeImportStep}
          situationChoice={onboarding.situationChoice}
          onContinueFromDone={() =>
            onboarding.setViewStep(viewStep + 1)
          }
        />
      </OnboardingStep>
    );
  }

  if (
    viewStep === ownerPostCertViewSteps.cae &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    return (
      <OnboardingStep name="cae" onboarding={onboarding}>
        <OnboardingCaeStep
          missingCfeTypeCodes={missingOnboardingCaeCfeTypes}
          isCaeListRefreshing={onboardingCaeListRefreshing}
          onBack={onboarding.handleBackStep}
          onNext={onboarding.handleNextStep}
          nextDisabled={onboarding.nextButtonDisabled}
          nextLoading={onboarding.nextButtonLoading}
        />
      </OnboardingStep>
    );
  }

  if (
    needsCfeImportStep &&
    ownerPostCertViewSteps.cfe != null &&
    viewStep === ownerPostCertViewSteps.cfe &&
    !isAccountantFlowOwner &&
    !isAccountantUser &&
    tenant
  ) {
    const displayName =
      tenant.legalName?.trim() || tenant.name?.trim() || tenant.id;
    return (
      <OnboardingStep name="cfe" onboarding={onboarding}>
        <OnboardingCfeImportStep
          tenantId={tenant.id}
          tenantDisplayName={displayName}
          onImportSuccess={onboarding.markCfeImportUploadCompleted}
          onSkip={
            user?.role !== "accountant"
              ? onboarding.handleSkipCfeImportStep
              : undefined
          }
          onBack={onboarding.handleBackStep}
          onNext={onboarding.handleNextStep}
          nextDisabled={onboarding.nextButtonDisabled}
          nextLoading={onboarding.nextButtonLoading}
        />
      </OnboardingStep>
    );
  }


  if (
    ownerPostCertViewSteps.literalE !== null &&
    viewStep === ownerPostCertViewSteps.literalE &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    return (
      <OnboardingStep name="literal_e" onboarding={onboarding}>
        <LiteralEStep
          literalE={onboarding.literalE}
          setLiteralE={onboarding.setLiteralE}
          literalEProofFile={onboarding.literalEProofFile}
          setLiteralEProofFile={onboarding.setLiteralEProofFile}
        />
      </OnboardingStep>
    );
  }

  if (
    viewStep === ownerPostCertViewSteps.personalize &&
    !isAccountantFlowOwner &&
    !isAccountantUser &&
    onboarding.tenant
  ) {
    return (
      <OnboardingStep name="personalize" onboarding={onboarding}>
        <PersonalizeStep
          tenant={onboarding.tenant}
          legalName={onboarding.legalName}
          setLegalName={onboarding.setLegalName}
          onLogoFileChange={onboarding.handleLogoFileChange}
          uploadingLogo={onboarding.uploadingLogo}
        />
      </OnboardingStep>
    );
  }

  if (
    isMigrating &&
    viewStep === ownerPostCertViewSteps.migrationOrPlanFirst &&
    !isAccountantFlowOwner &&
    !isAccountantUser
  ) {
    return (
      <OnboardingStep name="plan_migrating" onboarding={onboarding}>
        <MigrationChecklistStep
          supportRequestSentMigration={onboarding.supportRequestSentMigration}
          onRequestMigrationSupport={onboarding.handleRequestMigrationSupport}
          requestingSupport={onboarding.requestingSupport}
        />
      </OnboardingStep>
    );
  }

  if (
    !isAccountantUser &&
    ((!isMigrating &&
      viewStep === ownerPostCertViewSteps.migrationOrPlanFirst) ||
      (isMigrating && viewStep === ownerPostCertViewSteps.planMigrating))
  ) {
    return (
      <OnboardingStep name="plan" onboarding={onboarding}>
        <OnboardingShell
          variant="single"
          form={
            <>
              <OnboardingBrandMark />
              <PlanStep
                plans={onboarding.plans}
                partnerBenefit={onboarding.partnerBenefit}
                selectedPlanId={onboarding.selectedPlanId}
                selectedBillingInterval={onboarding.selectedBillingInterval}
                setSelectedBillingInterval={onboarding.setSelectedBillingInterval}
                onSelectPlan={onboarding.setSelectedPlanId}
                eligiblePlanIds={onboarding.tenant?.eligiblePlanIds ?? null}
              />
              <div className="fx-onb-actions" style={{ marginTop: 28 }}>
                <div className="side">
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-secondary"
                    onClick={onboarding.handleBackStep}
                  >
                    <ArrowLeft aria-hidden />
                    {t("onboarding.back", "Volver")}
                  </button>
                </div>
                <div className="side end">
                  <button
                    type="button"
                    className="fx-onb-btn fx-onb-btn-accent"
                    onClick={() => void onboarding.handleNextStep()}
                    disabled={
                      onboarding.nextButtonDisabled ||
                      onboarding.nextButtonLoading
                    }
                  >
                    {onboarding.nextButtonLoading ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {t("onboarding.next", "Siguiente")}
                    <ArrowRight aria-hidden />
                  </button>
                </div>
              </div>
            </>
          }
        />
      </OnboardingStep>
    );
  }

  if (viewStep === totalSteps && !isAccountantFlowOwner) {
    return (
      <OnboardingStep name="done" onboarding={onboarding}>
        <ReadyStep
          onGoToDashboard={onboarding.handleFinish}
          finishing={onboarding.finishing}
          tenant={onboarding.tenant ?? null}
        />
      </OnboardingStep>
    );
  }

  return <></>;
}
